/**
 * Per-day display state for a habit — shared by the list (what to show / where)
 * and the header progress ring. Pure functions, no React.
 *
 * See .claude/tasks/habits-feature.md (D9 backfill, D10b flat list, D11 quota,
 * D15 header progress).
 */
import type { Habit } from '../db/models'
import { dateKey } from '../date/calendar'
import { isHabitDueOn, weekCompletionCount } from './recurrence'

export interface HabitDayState {
  /** Should this habit appear in the list for the given day? */
  appears: boolean
  /** Checkbox state for that specific day. */
  completed: boolean
  /** Belongs in the "done" zone at the bottom (sinks). */
  done: boolean
  /** Flexible weekly-quota habit? */
  isFlexible: boolean
  /** For flexible habits: completions this week and the target. */
  quotaDone: number
  quotaTotal: number
  quotaMet: boolean
}

// `weekday` here is the helper's 0=Mon scheme — date.ts handles it.
export function habitDayState(
  habit: Pick<Habit, 'frequency' | 'startDate' | 'pausedAt' | 'deletedAt'>,
  completedKeys: Set<string>,
  day: Date,
): HabitDayState {
  const key = dateKey(day)
  const completed = completedKeys.has(key)

  if (habit.frequency.kind === 'timesPerWeek') {
    const total = habit.frequency.count
    const quotaDone = weekCompletionCount(completedKeys, day)
    const quotaMet = quotaDone >= total
    return {
      appears: !habit.pausedAt && !habit.deletedAt,
      completed,
      done: quotaMet,
      isFlexible: true,
      quotaDone,
      quotaTotal: total,
      quotaMet,
    }
  }

  const due = isHabitDueOn(habit, day)
  return {
    appears: due,
    completed,
    done: completed,
    isFlexible: false,
    quotaDone: 0,
    quotaTotal: 0,
    quotaMet: false,
  }
}

/**
 * Header progress for "today": how many of today's due habits are done.
 * Flexible habits count toward the total while their weekly quota is unmet,
 * and leave the count once met (D15).
 */
export function todayProgress(
  habits: Array<Pick<Habit, 'frequency' | 'startDate' | 'pausedAt' | 'deletedAt' | 'id'>>,
  completedByHabit: Map<string, Set<string>>,
  today: Date,
): { done: number; total: number } {
  let done = 0
  let total = 0
  for (const habit of habits) {
    if (habit.pausedAt || habit.deletedAt) continue
    const keys = completedByHabit.get(habit.id) ?? new Set<string>()
    const state = habitDayState(habit, keys, today)
    if (habit.frequency.kind === 'timesPerWeek') {
      if (state.quotaMet) continue // left today's count
      total++
      if (state.completed) done++
    } else if (state.appears) {
      total++
      if (state.completed) done++
    }
  }
  return { done, total }
}
